import { TILE } from '@/gfx/tiles';
import { dist } from '@/engine/math';
import { idx, roomAtPx, type Floor, type PropEnt } from '@/world/builder';
import { boxFree, tileOf } from './physics';
import { findPath } from './nav';

export interface HidingPlace { prop: PropEnt; x: number; y: number }

/** Choose a nearby entrance the friend can actually reach, including side entrances. */
export function reachableHidingPlace(f: Floor, x: number, y: number, maxD: number): HidingPlace | null {
  const doorRule = (d: Floor['doors'][number]) => d.open || !d.locked && d.revealed;
  const passDoor = (tx: number, ty: number) => {
    const d = f.doorAt.get(idx(f, tx, ty));
    return !!d && doorRule(d);
  };
  let best: HidingPlace | null = null, bestDistance = maxD * 1.5;
  for (const prop of f.props) {
    if (!prop.kind.hide || prop.occupants.length >= prop.kind.hide.cap) continue;
    const left = prop.x * TILE, top = prop.y * TILE;
    const right = left + prop.kind.fw * TILE, bottom = top + prop.kind.fh * TILE;
    const cx = (left + right) / 2, cy = (top + bottom) / 2;
    if (Math.hypot(Math.max(left - x, 0, x - right), Math.max(top - y, 0, y - bottom)) > maxD) continue;
    for (const [ex, ey] of [[cx, bottom + 8], [left - 8, cy], [right + 8, cy], [cx, top - 8]]) {
      const direct = dist(x, y, ex, ey);
      if (direct > maxD || direct >= bestDistance || roomAtPx(f, ex, ey)?.id !== prop.room || !boxFree(f, ex, ey, doorRule)) continue;
      const path = findPath(f, tileOf(x), tileOf(y), tileOf(ex), tileOf(ey), passDoor, 800);
      if (!path) continue;
      let distance = 0, px = x, py = y;
      for (const [tx, ty] of path) {
        const nx = tx * TILE + 8, ny = ty * TILE + 8;
        distance += dist(px, py, nx, ny); px = nx; py = ny;
      }
      distance += dist(px, py, ex, ey);
      if (distance < bestDistance) { bestDistance = distance; best = { prop, x: ex, y: ey }; }
    }
  }
  return best;
}
